import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { AppRoutingModule } from './app-routing.module';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient } from '@angular/common/http';
import { AntdModule } from './Antd.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { SharedModule } from './shared/shared.module';
import { QueryComponent } from './query-interface/components/query/query.component';
import { QueryBuilderComponent } from './query-interface/components/query-builder/query-builder.component';
import { QueryHistoryComponent } from './query-interface/components/query-history/query-history.component';
import { AudienceExportHistoryComponent } from './query-interface/components/audience-export-history/audience-export-history.component';
import { ActivationChannelListComponent } from './query-interface/components/activation-channel-list/activation-channel-list.component';
import { QueryResultComponent } from './query-interface/components/query-result/query-result.component';
import { DataIngestionComponent } from './data-ingestion/components/data-ingestion/data-ingestion.component';
import { FileListComponent } from './data-ingestion/components/file-list/file-list.component';
import { FileUploadComponent } from './data-ingestion/components/file-upload/file-upload.component';
import { DataTableComponent } from './data-ingestion/components/data-table/data-table.component';
import { AppConfigService } from './shared/services/app-config.service';
import { AuthService } from './shared/services/auth.service';
import { QueryDataTableComponent } from './query-interface/components/query-data-table/query-data-table.component';
import { HomeComponent } from './home/home.component';
import { ViewDetailsModalComponent } from './query-interface/components/view-details-modal/view-details-modal.component';
import { ListQueryResultComponent } from './query-interface/components/list-query-result/list-query-result.component';
import { AggregateQueryResultComponent } from './query-interface/components/aggregate-query-result/aggregate-query-result.component';
import { CreateActivationChannelModalComponent } from './query-interface/components/create-activation-channel-modal/create-activation-channel-modal.component';
import { ExportAudienceModalComponent } from './query-interface/components/export-audience-modal/export-audience-modal.component';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { DataConnectionComponent } from './data-connection/components/data-connection/data-connection.component';
import { CreateDataConnectionComponent } from './data-connection/components/create-data-connection/create-data-connection.component';
import { DatasourceSelectionComponent } from './data-connection/components/datasource-selection/datasource-selection.component';
import { DataConnectionStepperComponent } from './data-connection/components/data-connection-stepper/data-connection-stepper.component';
import { DataConnectionDetailsComponent } from './data-connection/components/data-connection-details/data-connection-details.component';
import { DataConnectionMappingsComponent } from './data-connection/components/data-connection-mappings/data-connection-mappings.component';
import { UsersComponent } from './users/components/users/users.component';
import { ListUsersComponent } from './users/components/list-users/list-users.component';
import { CreateUserComponent } from './users/components/create-user/create-user.component';
import { BiComponent } from './bi/component/bi/bi.component';
import { BuildChartComponent } from './bi/component/build-chart/build-chart.component';
import { ManageChartsComponent } from './bi/component/manage-charts/manage-charts.component';
import { CreateDashboardComponent } from './bi/component/create-dashboard/create-dashboard.component';
import { ManageDashboardsComponent } from './bi/component/manage-dashboards/manage-dashboards.component';
import { ChartDetailsComponent } from './bi/component/chart-details/chart-details.component';
import { ChartDataPreparerComponent } from './bi/component/chart-data-preparer/chart-data-preparer.component';
import { ChartConfigComponent } from './bi/component/chart-config/chart-config.component';
import { ChartPreviewComponent } from './bi/component/chart-preview/chart-preview.component';
import { DashboardPreviewComponent } from './bi/component/dashboard-preview/dashboard-preview.component';
import { DataPreviewComponent } from './bi/component/data-preview/data-preview.component';
import { MlComponent } from './ml/components/ml/ml.component';
import { BuildModelComponent } from './ml/components/build-model/build-model.component';
import { ModelHistoryComponent } from './ml/components/model-history/model-history.component';
import { ViewModelComponent } from './ml/components/view-model/view-model.component';
import { CollaborationComponent } from './collaboration/components/collaboration/collaboration.component';
import { CollaborationListComponent } from './collaboration/components/collaboration-list/collaboration-list.component';
import { CreateCollaborationComponent } from './collaboration/components/create-collaboration/create-collaboration.component';
import { CollaborationStepperComponent } from './collaboration/components/collaboration-stepper/collaboration-stepper.component';
import { CollaborationDetailsComponent } from './collaboration/components/collaboration-details/collaboration-details.component';
import { CollaborationDatasetComponent } from './collaboration/components/collaboration-dataset/collaboration-dataset.component';
import { CollaborationInvitationComponent } from './collaboration/components/collaboration-invitation/collaboration-invitation.component';
import { CollaborationSummaryComponent } from './collaboration/components/collaboration-summary/collaboration-summary.component';
import { AcceptInvitationComponent } from './collaboration/components/accept-invitation/accept-invitation.component';
import { ViewCollaborationInviteComponent } from './collaboration/components/view-collaboration-invite/view-collaboration-invite.component';
import { CollaborationViewDatasetModalComponent } from './collaboration/components/collaboration-view-dataset-modal/collaboration-view-dataset-modal.component';
registerLocaleData(en);

const appConfigInit = (appConfig: AppConfigService, authService: AuthService) => {
  return () => {
    return appConfig.loadAppConfig(authService);
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function authInterceptorInit(auth: AuthInterceptor): () => void {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  return () => {};
}

@NgModule({
  declarations: [
    AppComponent,
    QueryComponent,
    QueryBuilderComponent,
    QueryHistoryComponent,
    AudienceExportHistoryComponent,
    ActivationChannelListComponent,
    QueryResultComponent,
    DataIngestionComponent,
    FileListComponent,
    FileUploadComponent,
    DataTableComponent,
    QueryDataTableComponent,
    HomeComponent,
    ViewDetailsModalComponent,
    AggregateQueryResultComponent,
    ListQueryResultComponent,
    CreateActivationChannelModalComponent,
    ExportAudienceModalComponent,
    DataConnectionComponent,
    CreateDataConnectionComponent,
    DatasourceSelectionComponent,
    DataConnectionStepperComponent,
    DataConnectionDetailsComponent,
    DataConnectionMappingsComponent,
    UsersComponent,
    ListUsersComponent,
    CreateUserComponent,
    BiComponent,
    BuildChartComponent,
    ManageChartsComponent,
    CreateDashboardComponent,
    ManageDashboardsComponent,
    ChartDetailsComponent,
    ChartDataPreparerComponent,
    ChartConfigComponent,
    ChartPreviewComponent,
    DashboardPreviewComponent,
    DataPreviewComponent,
    MlComponent,
    BuildModelComponent,
    ModelHistoryComponent,
    ViewModelComponent,
    CollaborationComponent,
    CollaborationListComponent,
    CreateCollaborationComponent,
    CollaborationStepperComponent,
    CollaborationDetailsComponent,
    CollaborationDatasetComponent,
    CollaborationInvitationComponent,
    CollaborationSummaryComponent,
    ViewCollaborationInviteComponent,
    AcceptInvitationComponent,
    CollaborationViewDatasetModalComponent,
  ],
  imports: [
    BrowserModule,
    RouterModule,
    AppRoutingModule,
    AntdModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    SharedModule,
  ],
  bootstrap: [AppComponent],
  providers: [
    provideAnimationsAsync(),
    provideHttpClient(),
    { provide: APP_INITIALIZER, useFactory: appConfigInit, multi: true, deps: [AppConfigService, AuthService] },
    { provide: APP_INITIALIZER, useFactory: authInterceptorInit, multi: true, deps: [AuthInterceptor] },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
